export const STANDARDS_CATEGORY = "Standards";

export type GroupableServiceProduct = {
  id: string;
  name: string;
  category?: string | null;
  sortIndex?: number | null;
};

export type ServiceProductGroup<T extends GroupableServiceProduct> = {
  category: string;
  items: T[];
};

function categoryKey(value: string | null | undefined): string {
  return (value ?? "").trim();
}

function compareName(a: string, b: string): number {
  return a.toLowerCase().localeCompare(b.toLowerCase());
}

export function groupServiceProducts<T extends GroupableServiceProduct>(
  products: T[],
  categoryOrder: string[] = []
): Array<ServiceProductGroup<T>> {
  const map = new Map<string, T[]>();
  for (const product of products) {
    const key = categoryKey(product.category);
    const list = map.get(key) ?? [];
    list.push(product);
    map.set(key, list);
  }
  for (const items of map.values()) {
    items.sort((a, b) => {
      const byIndex = (a.sortIndex ?? 0) - (b.sortIndex ?? 0);
      if (byIndex !== 0) return byIndex;
      return compareName(a.name, b.name);
    });
  }

  const remaining = new Set(
    [...map.keys()].filter((k) => k.length > 0 && k !== STANDARDS_CATEGORY)
  );
  const ordered: string[] = [];
  if (map.has(STANDARDS_CATEGORY)) {
    ordered.push(STANDARDS_CATEGORY);
  }
  for (const raw of categoryOrder) {
    const name = raw.trim();
    if (!name || name === STANDARDS_CATEGORY) continue;
    if (remaining.delete(name)) ordered.push(name);
  }
  ordered.push(...[...remaining].sort(compareName));
  if (map.has("")) ordered.push("");

  return ordered
    .filter((key) => map.has(key))
    .map((category) => ({ category, items: map.get(category)! }));
}
